'use client';

import * as React from 'react';
import {
  Building2, ChevronRight, CheckCircle2, Globe, Mail,
  MapPin, Phone, ExternalLink, Users
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface School {
  id: number;
  name?: string;
  logo_url?: string;
  email?: string;
  website?: string;
  address?: string;
  slogan?: string;
  about_us?: string;
  telephone?: string;
  tenant_type?: string;
  tenant_id?: number;
  profile_setup?: boolean;
}

interface Assignment {
  id: number;
  assigned_schools?: School[] | School | number[];
  isFallback?: boolean;
  [key: string]: any;
}

interface ProjectInstitutesProps {
  assignments: Assignment[];
  project?: any;
  activeAssignmentId: number | undefined;
  onSelect: (assignmentId: number) => void;
}

/** Resolve a full School object from root project's assigned_schools by ID */
// function resolveSchool(
//   rawSchool: School | number | undefined,
//   rootSchools: School[]
// ): School | null {
//   if (!rawSchool) return null;
//   if (typeof rawSchool === 'object') return rawSchool as School;
//   // It's just an ID — look it up in the root project's full school list
//   return rootSchools.find((s) => s.id === rawSchool) ?? null;
// }

export function ProjectInstitutes({
  assignments,
  project,
  activeAssignmentId,
  onSelect,
}: ProjectInstitutesProps) {
  // Full school objects from the root project API (always has name, logo, etc.)
  const rootSchools: School[] = React.useMemo(() => {
    const s = project?.assigned_schools;
    if (!s) return [];
    return Array.isArray(s) ? s : [s];
  }, [project?.assigned_schools]);

  // Pair each root school (full detail) with an assignment record (for its ID)
  // If no assignments, fall back to showing schools without selection
  const displayList = React.useMemo(() => {
    if (rootSchools.length > 0) {
      return rootSchools.map((school, idx) => ({
        school,
        assignment: assignments[idx] ?? { id: school.id, isFallback: true },
      }));
    }
    // No root school data — use assignments as-is
    return assignments.map((a) => {
      const raw = Array.isArray(a.assigned_schools) ? a.assigned_schools[0] : a.assigned_schools;
      const school: School | null = typeof raw === 'object' && raw !== null ? raw : null;
      return { school, assignment: a };
    });
  }, [rootSchools, assignments]);

  if (displayList.length === 0) {
    return (
      <div className="text-center py-20 border-2 border-dashed border-border/40 rounded-3xl bg-muted/5 animate-in fade-in duration-500">
        <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
        <h3 className="text-xl font-bold text-foreground/70">No Institutes Assigned</h3>
        <p className="text-sm text-muted-foreground mt-1">
          This project hasn't been assigned to any schools yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-primary" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
          {displayList.length} Institute{displayList.length !== 1 ? 's' : ''} Assigned
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {displayList.map(({ assignment, school }) => {
          const isActive = activeAssignmentId === assignment.id;
          const schoolName = school?.name || 'Unknown Institute';
          const schoolLogo = school?.logo_url;

          return (
            <Card
              key={assignment.id}
              className={cn(
                'group relative overflow-hidden transition-all duration-300 rounded-2xl border-border/40 bg-card/50 backdrop-blur-xl',
                'hover:shadow-xl hover:border-primary/20',
                isActive && 'ring-2 ring-primary border-primary/30 shadow-lg shadow-primary/10'
              )}
            >


              <CardContent className="p-5 space-y-4">
                {/* School header */}
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <div className={cn(
                    'w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0',
                    'border border-border/40 bg-muted/20',
                    isActive && 'ring-2 ring-primary/30 bg-primary/5'
                  )}>
                    {schoolLogo ? (
                      <img src={schoolLogo} alt={schoolName} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className={cn('w-7 h-7', isActive ? 'text-primary' : 'text-muted-foreground/50')} />
                    )}
                  </div>

                  {/* Name + slogan */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-foreground leading-tight truncate">
                        {schoolName}
                      </h3>
                      {isActive && (
                        <Badge className="bg-primary/10 text-primary border-primary/20 rounded-full text-[9px] px-2 py-0 h-4 font-black uppercase tracking-wider flex items-center gap-1 shrink-0">
                          <CheckCircle2 className="w-2.5 h-2.5" />
                          Active
                        </Badge>
                      )}
                    </div>
                    {school?.slogan && (
                      <p className="text-[11px] text-muted-foreground/70 mt-0.5 italic truncate">
                        "{school.slogan}"
                      </p>
                    )}
                    {school?.tenant_type && (
                      <Badge variant="secondary" className="mt-1 text-[9px] rounded-full px-2 h-4 uppercase tracking-wider font-bold">
                        {school.tenant_type}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* School details grid */}
                <div className="space-y-2 bg-muted/20 rounded-xl p-3 border border-border/20">
                  {school?.email && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Mail className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                      <span className="text-muted-foreground truncate">{school.email}</span>
                    </div>
                  )}
                  {school?.website && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Globe className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                      <a
                        href={school.website.startsWith('http') ? school.website : `https://${school.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary/70 hover:text-primary truncate flex items-center gap-1 transition-colors"
                      >
                        {school.website}
                        <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                      </a>
                    </div>
                  )}
                  {school?.address && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <MapPin className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                      <span className="text-muted-foreground truncate">{school.address}</span>
                    </div>
                  )}
                  {school?.telephone && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Phone className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                      <span className="text-muted-foreground">{school.telephone}</span>
                    </div>
                  )}
                </div>

                {/* About us snippet */}
                {school?.about_us && (
                  <p className="text-[11px] text-muted-foreground/70 leading-relaxed line-clamp-2 px-1">
                    {school.about_us}
                  </p>
                )}

                {/* Action footer */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[10px] text-muted-foreground/40 font-mono">
                    #{assignment.isFallback ? `school-${assignment.id}` : `assign-${assignment.id}`}
                  </span>
                  <Button
                    onClick={() => onSelect(assignment.id)}
                    variant={isActive ? 'outline' : 'default'}
                    size="sm"
                    className={cn(
                      'h-8 text-xs rounded-full px-4 transition-all duration-300',
                      isActive
                        ? 'border-primary/30 text-primary hover:bg-primary/5'
                        : 'shadow-md hover:shadow-primary/20'
                    )}
                  >
                    {isActive ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 mr-1.5" />
                        Selected
                      </>
                    ) : (
                      <>
                        View Progress
                        <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>

              {/* Decorative bg icon */}
              <div className="absolute top-0 right-0 p-4 opacity-[0.025] group-hover:opacity-[0.06] transition-opacity duration-500 pointer-events-none">
                <Building2 className="w-28 h-28 rotate-12" />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
